/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (campaign #18, Track B). The back-office
 * UI uses this to (a) decide whether a user may enter the admin area at all and
 * (b) hide/disable individual actions a role can't perform. The backend remains
 * the source of truth and re-checks every mutation; this is purely a UX layer so
 * the client never offers an action that would 403.
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags, but not destructive removals or granting
 *                   privileged roles
 *   - support     — read users + audit, ban/unban + revoke sessions (no role/flag
 *                   changes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

import RBAC_CONTRACT from "./rbac-contract.json";

export const ADMIN_ROLES = ["super_admin", "admin", "support", "read_only", "user"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on. */
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  | "users.remove"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "metrics.read"
  /**
   * Mutate back-office content: themes, fonts, email templates and campaigns.
   * Mirror of `Capability::ManageContent`. Previously missing from this list,
   * so UI could not hide those actions from `read_only` — and backend
   * also did not gate them: they were executable for real.
   */
  | "content.write";

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "metrics.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  "integrations.testEmail",
  "content.write",
];

const ADMIN_CAPS: AdminCapability[] = [...SUPPORT_CAPS, "users.setRole", "flags.write"];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  read_only: READ_CAPS,
  user: [],
};

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role. */
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none). */
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability. */
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all. */
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

/**
 * Hierarchical order of roles, READ from the shared contract — not copied.
 *
 * The model must exist across two runtimes (server decides, UI hides
 * what the server will reject), but the contract is unique:
 * `rbac-contract.json`. Rust reads it in tests and asserts that `Role::rank()`
 * matches; here it is used directly at runtime, so this side
 * cannot fall out of sync.
 *
 * Written key by key rather than `Object.fromEntries` by design: the compiler
 * verifies that the contract provides all five roles, rather than
 * masking it with an unchecked `as Record<...>`.
 */
const ROLE_RANK: Record<AdminRole, number> = {
  super_admin: RBAC_CONTRACT.roles.super_admin.rank,
  admin: RBAC_CONTRACT.roles.admin.rank,
  support: RBAC_CONTRACT.roles.support.rank,
  read_only: RBAC_CONTRACT.roles.read_only.rank,
  user: RBAC_CONTRACT.roles.user.rank,
};

/**
 * Roles that `actorRole` can GRANT from the user manager.
 *
 * Replaces the old constant `ASSIGNABLE_ROLES`, which was simply
 * `ADMIN_ROLES` — the entire list, for anyone. The name did not require
 * saying "assignable by whom?", so the role selector offered
 * `super_admin` to an `admin`, and the backend accepted it without any check:
 * a single-step privilege escalation.
 *
 * Rule (identical to `can_grant_role` in the backend): requires the
 * `users.setRole` capability and is never granted above one's own rank.
 */
export function assignableRolesFor(actorRole: string | null | undefined): readonly AdminRole[] {
  if (!roleCan(actorRole, "users.setRole")) {
    return [];
  }
  const actorRank = ROLE_RANK[toAdminRole(actorRole)];
  return ADMIN_ROLES.filter((role) => ROLE_RANK[role] <= actorRank);
}

/**
 * Can `actorRole` administer a user whose CURRENT role is `targetRole`?
 * Mirror of `can_administer`: strictly below, except for `super_admin`,
 * who can administer peers.
 */
export function canAdministerUser(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toAdminRole(actorRole);
  if (actor === "super_admin") {
    return true;
  }
  return ROLE_RANK[toAdminRole(targetRole)] < ROLE_RANK[actor];
}

/** Shared contract re-exported for cross-check tests. */
export { RBAC_CONTRACT };
